import {
  type CompositionV3Ent,
  EntKind,
  POSTING_SOURCE_IDS,
  type PostingsV3DataQueryVariables,
  type PostingsV3Filters,
  PostingsV3Mode,
} from '@revelio/data-access';

import type { Filter, PrimaryFilterId } from '../types/filters';
import { FilterId } from '../types/filters';
import { roleCountsLevelFromCompositionFilters } from './composition-v3/getCompositionV3BreakdownOptions';
import { getCompositionV3Filters } from './composition-v3/getCompositionV3Filters';
import { normalizeCompositionEffectiveFilters } from './composition-v3/getCompositionV3Variables';
import { getV3Entities } from './get-v3-entities';
import { getV3Taxonomies } from './get-v3-taxonomies';
import { getPostingFilters } from './getPostingFilters';
import { stripEntitiesFromFilters } from './role-skill-ent-kind';

/**
 * Empty filter values (`[]` / `[[]]`) mean "no filter" but still perturb the
 * urql cache key. Drop them so the request stays byte-identical whether or not
 * a caller threads empty entries through (e.g. the landing prefetch), keeping
 * the warmed cache and the provider's request aligned.
 */
const stripEmptyFilterValues = (
  filters: PostingsV3Filters
): PostingsV3Filters =>
  Object.fromEntries(
    Object.entries(filters).filter(([, value]) => {
      if (!Array.isArray(value)) return true;
      return (
        value.length > 0 &&
        !value.every((inner) => Array.isArray(inner) && inner.length === 0)
      );
    })
  ) as PostingsV3Filters;

/**
 * Maps an entity's `EntKind` back to its primary filter dimension. The filter
 * builders need it to exclude the primary selections from `filters` (the
 * entities already cover them) and to resolve `dim1` for the Indeed provider's
 * geo/role-specific min start date.
 */
const entKindToPrimaryDimension = (
  entType: EntKind
): PrimaryFilterId | null => {
  switch (entType) {
    case EntKind.Company:
    case EntKind.RicsK10:
    case EntKind.RicsK50:
    case EntKind.RicsK400:
      return FilterId.COMPANY;
    case EntKind.Region:
    case EntKind.Country:
    case EntKind.State:
    case EntKind.Msa:
    case EntKind.City:
      return FilterId.GEOGRAPHY;
    case EntKind.RoleLvl_1:
    case EntKind.RoleLvl_2:
    case EntKind.RoleLvl_3:
    case EntKind.RoleLvl_4:
    case EntKind.RoleLvl_5:
    case EntKind.RoleLvl_6:
    case EntKind.RoleLvl_7:
      return FilterId.ROLE;
    default:
      return null;
  }
};

/**
 * Resolves the primary dimension from the requested entities. The map's geo
 * entities aren't a 1:1 copy of the primary filter (countries get enriched with
 * their MSAs), so it can't be inferred from the filter tree. Defaults to
 * GEOGRAPHY — the only entity-less caller is the map.
 */
const primaryDimensionFromEntities = (
  entities: ReadonlyArray<CompositionV3Ent>
): PrimaryFilterId =>
  (entities[0] && entKindToPrimaryDimension(entities[0].EntType)) ??
  FilterId.GEOGRAPHY;

/**
 * Builds `postings_v3` variables from an EXPLICIT entity list. Used by the map
 * (its geographies-to-plot are enriched beyond the raw primary selections) and
 * as the delegate for {@link getPostingsV3Variables}. Entity ids are stripped
 * from `filters` so they live only in `req.entities` and don't double-filter.
 */
export const buildPostingsV3VariablesFromEntities = ({
  entities,
  effectiveFilters,
  isSnapshot = false,
  defaultMetricMode,
  activeFilterMap,
  primaryDimension,
}: {
  entities: CompositionV3Ent[];
  effectiveFilters: Filter[];
  isSnapshot?: boolean;
  defaultMetricMode?: 'posting' | 'expected_hires';
  activeFilterMap?: ReadonlyMap<FilterId, Filter>;
  /**
   * Explicit primary dimension; derived from entity kinds when omitted. The
   * dashboard provider and geo map pass it so enrichment and the request agree
   * on the dimension instead of relying on entity-kind derivation.
   */
  primaryDimension?: PrimaryFilterId;
}): PostingsV3DataQueryVariables => {
  const normalized = normalizeCompositionEffectiveFilters(effectiveFilters);
  const resolvedPrimaryDimension =
    primaryDimension ?? primaryDimensionFromEntities(entities);

  const compositionFilters = getCompositionV3Filters({
    primaryDimension: resolvedPrimaryDimension,
    isSnapshot,
    effectiveFilters: normalized,
  });

  const filtersWithoutEntities = stripEntitiesFromFilters(
    compositionFilters,
    entities
  );

  const { role_taxonomy, skill_taxonomy } = getV3Taxonomies(
    normalized,
    activeFilterMap
  );

  const postingFilters = getPostingFilters(
    resolvedPrimaryDimension,
    effectiveFilters,
    {
      isSnapshot,
      defaultMetricMode,
    }
  );

  // Postings are job ads, not people, so demographics (gender/ethnicity/
  // highest_degree) are excluded — they were a backend mistake being removed.
  // keywords/raw_title remap to `title`; month dates become weekly clamped dates.
  const {
    keywords: _keywords,
    raw_title,
    start_date: _start_date,
    end_date: _end_date,
    gender: _gender,
    ethnicity: _ethnicity,
    highest_degree: _highest_degree,
    ...shared
  } = filtersWithoutEntities;

  const provider = (
    postingFilters.provider ?? [POSTING_SOURCE_IDS.unified]
  ).filter((id): id is number => typeof id === 'number');

  const filters = stripEmptyFilterValues({
    ...shared,
    ...(raw_title ? { title: raw_title } : {}),
    provider: provider.length > 0 ? provider : [POSTING_SOURCE_IDS.unified],
    start_date: postingFilters.start_date,
    end_date: postingFilters.end_date,
  });

  const mode =
    (postingFilters.metric_mode ?? defaultMetricMode) === 'expected_hires'
      ? PostingsV3Mode.Hires
      : PostingsV3Mode.Normal;

  const roles_counts_level = roleCountsLevelFromCompositionFilters(
    filtersWithoutEntities
  );

  return {
    req: {
      entities,
      filters,
      options: { mode, roles_counts_level },
      role_taxonomy,
      skill_taxonomy,
    },
  };
};

/**
 * Builds `postings_v3` variables, deriving `req.entities` from the PRIMARY
 * FILTER selections, then delegating to {@link buildPostingsV3VariablesFromEntities}.
 */
export const getPostingsV3Variables = ({
  primaryDimension,
  effectiveFilters,
  isSnapshot = false,
  defaultMetricMode,
  activeFilterMap,
}: {
  primaryDimension: PrimaryFilterId;
  effectiveFilters: Filter[];
  isSnapshot?: boolean;
  defaultMetricMode?: 'posting' | 'expected_hires';
  activeFilterMap?: ReadonlyMap<FilterId, Filter>;
}): PostingsV3DataQueryVariables => {
  const entities = getV3Entities({
    primaryDimension,
    filters: normalizeCompositionEffectiveFilters(effectiveFilters),
  });

  return buildPostingsV3VariablesFromEntities({
    entities,
    effectiveFilters,
    isSnapshot,
    defaultMetricMode,
    activeFilterMap,
    primaryDimension,
  });
};
